import { useMemo } from "react";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Globe,
  MessageCircle,
  Send,
  AtSign,
} from "lucide-react";
import { useContactInfo, useSocialLinks } from "@/hooks/use-supabase-data";

const fallbackContactInfo = {
  title: "Sandalpur Nagar Village",
  description:
    "A thriving community dedicated to progress, unity, and service. Building a better future together.",
  phone: "+91 98765 43210",
  email: "sandalpur@gmail.com",
  address: "Village Office, Sandalpur, District Headquarters, State, India",
};

const fallbackSocialLinks = [
  { id: "facebook", platform: "Facebook", url: "https://www.facebook.com", icon: "facebook" },
  { id: "instagram", platform: "Instagram", url: "https://www.instagram.com", icon: "instagram" },
];

const socialIconMap: Record<string, typeof Facebook> = {
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  whatsapp: MessageCircle,
  telegram: Send,
  threads: AtSign,
  website: Globe,
  globe: Globe,
};

export function Footer() {
  const { contactInfo } = useContactInfo();
  const { socialLinks } = useSocialLinks();

  const resolvedContactInfo = contactInfo ?? fallbackContactInfo;

  const resolvedSocialLinks = useMemo(() => {
    const activeLinks = (socialLinks || []).filter((link) => link.is_active ?? true);
    if (activeLinks.length === 0) {
      return fallbackSocialLinks;
    }
    return activeLinks;
  }, [socialLinks]);

  return (
    <footer className="bg-background mt-16 py-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 rounded-2xl bg-card neo-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-poppins text-lg font-bold text-foreground mb-4 flex items-center gap-2" data-testid="heading-map">
                <MapPin className="w-5 h-5 text-primary" />
                How to Reach
              </h4>
              <div className="w-full h-64 rounded-lg overflow-hidden border border-border" data-testid="footer-map">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d544.1054101452854!2d78.7291937647229!3d28.67449379486115!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ae300618359ab%3A0xa97442f1c6a4dc8!2sSandalpur%20Gram%20Panchayat!5e1!3m2!1sen!2sin!4v1768643426367!5m2!1sen!2sin" 
                width="100%" 
                height="100%" 
                style={{border:0}} 
                allowFullScreen loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Location Map"
              />
              </div>
            </div>

            <div>
              <h4 className="font-poppins text-lg font-bold text-foreground mb-4" data-testid="heading-contact">
                Contact Information
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-foreground" data-testid="contact-footer-phone">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="font-inter">{resolvedContactInfo.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground" data-testid="contact-footer-email">
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="font-inter">{resolvedContactInfo.email}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-poppins text-lg font-bold text-foreground mb-4" data-testid="heading-social">
                Connect With Us
              </h4>
              <p className="font-inter text-sm text-muted-foreground mb-4" data-testid="text-social-description">
                Stay updated with the latest news and events from Sandalpur Village.
              </p>
              <div className="flex items-center gap-3 flex-wrap" data-testid="social-links">
                {resolvedSocialLinks.map((link) => {
                  const iconKey = link.icon?.toLowerCase?.() ?? "";
                  const IconComponent = socialIconMap[iconKey] ?? Globe;

                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Sandalpur Village on ${link.platform}`}
                      className="p-2 rounded-full border border-border text-foreground transition-transform duration-200 hover:-translate-y-1 hover:text-primary hover:border-primary"
                      data-testid="social-link"
                    >
                      <IconComponent className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-muted mt-8 pt-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-2 h-6 bg-primary rounded-sm" />
              <div className="w-2 h-6 bg-white border border-border rounded-sm" />
              <div className="w-2 h-6 bg-secondary rounded-sm" />
            </div>
            <p className="font-inter text-sm text-muted-foreground" data-testid="text-copyright">
              © {new Date().getFullYear()} Sandalpur. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
